// Plan-branding slice — the data seam for the header's plan logo. Lives centrally
// in store/slices (one slice per concern). The header logo used to be a faked
// first-letter of the plan name; this fetches the real branding (logoText + brand
// gradient/color) from the backend `/plans/{planId}` for the selected account's plan.
//
// It always calls GET /plans/{planId} and maps the branding to the UI shape —
// mock mode is gone (the backend's data/plans.json is the fixture source).
// Components read camelCase only (Frontend Rules: mappers always, boundary kept).
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getPlan } from '../../services/api/plans';

// ----- Mapper (the camelCase boundary) -----
// The /plans wire shape is already camelCase; we keep only the fields the header
// logo needs. `logoGradient`/`color` are plan-branding DATA (per-plan PMS colors
// from the backend), applied as an inline background — not authored CSS, so the
// "tokens only" rule doesn't apply (CLAUDE.md §16: localize at the source; data
// stays data).
export const mapPlanBrandingToUI = (api) => ({
  planId: api.planId,
  name: api.name,
  shortName: api.shortName ?? api.name,
  logoText: api.logoText ?? (api.name || 'P').charAt(0).toUpperCase(),
  logoUrl: api.logoUrl ?? null, // image logo; null → fall back to text/gradient
  color: api.color ?? null,
  logoGradient: api.logoGradient ?? null,
});

// ----- Thunk: the backend seam -----
// GET /plans/{planId} → mapPlanBrandingToUI. Arg stays { planId, planName } so
// existing callers don't change; the guard keys on planId.
export const fetchPlanBranding = createAsyncThunk(
  'planBranding/fetch',
  async ({ planId } = {}, { rejectWithValue }) => {
    try {
      if (!planId) return rejectWithValue('No plan selected.');
      return mapPlanBrandingToUI(await getPlan(planId));
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to load plan branding.');
    }
  },
  // Single-fetch guard (FRONTEND_RULES §7): skip when the SAME plan is already in
  // flight OR already loaded; switching to a different plan, or a retry after
  // failure, still runs. Keyed on planId (the arg is an object — never compare by
  // reference).
  {
    condition: ({ planId } = {}, { getState }) => {
      const s = getState().planBranding;
      if ((s.status === 'loading' || s.status === 'succeeded') && s.lastArg === planId) return false;
      return true;
    },
  },
);

const initialState = {
  data: null, // { planId, name, shortName, logoText, color, logoGradient } | null
  status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,
  lastArg: undefined, // the planId the current data is for — drives the single-fetch guard
};

const planBrandingSlice = createSlice({
  name: 'planBranding',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPlanBranding.pending, (state, action) => {
        state.status = 'loading';
        state.error = null;
        state.lastArg = action.meta.arg?.planId;
      })
      .addCase(fetchPlanBranding.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.data = action.payload;
      })
      .addCase(fetchPlanBranding.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to load plan branding';
      });
  },
});

// ----- Selectors -----
export const selectPlanBranding = (state) => state.planBranding.data;
export const selectPlanBrandingStatus = (state) => state.planBranding.status;
export const selectPlanBrandingError = (state) => state.planBranding.error;

export default planBrandingSlice.reducer;
